h<script lang="ts">
import { computed, defineComponent, reactive } from '@vue/composition-api';
import { isDevelopment } from '@/_core/utils/misc';
import { currentUserWsRoles, hasStorymapAccess } from '@/_core/utils/roles';

import { useCurrentWsStore } from '@/store/main/currentWs';
import { subscriptionManager, FeatureAddOn } from '@/_core/utils/features';

interface IProps {
    navDrawer: boolean;
    notificationsCount: number;
    isNotificationsPanelOpen: boolean;
}

const AppBar = defineComponent({
    name: 'AppBar',
    components: {},
    props: {
        navDrawer: Boolean,
        notificationsCount: Number,
        isNotificationsPanelOpen: Boolean,
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setup(props, ctx) {
        const store = useCurrentWsStore();
        const isInWsContext = computed(() => store.state.hasCurrentWs);
        const wsName = computed(() => store.state.name);
        const memberCount = computed(() => store.state.memberCount);
        const observerCount = computed(() => store.state.observerCount);

        const state = reactive({
            hasRightsToManageUsersOrWs:
                initAppData.isInWsContext && (currentUserWsRoles.isWorkspaceAdmin || currentUserWsRoles.isSubscriptionAdmin),
            hasRightsToGlobalAddButton:
                initAppData.isInWsContext &&
                (currentUserWsRoles.isWorkspaceAdmin || currentUserWsRoles.isSubscriptionAdmin || currentUserWsRoles.isWorkspaceMember),
            hasRoadmapsFeatureAddon: computed(() => subscriptionManager.hasFeatureAddOn(FeatureAddOn.BasicRoadmaps)),
        });

        const onOpenNavDrawer = () => {
            ctx.emit('openNavDrawer');
        };

        const openUserProfile = () => {
            ctx.emit('openUserProfile');
        };

        const onNotificationsPanelToggle = () => {
            ctx.emit('notificationsPanelToggle');
        };

        return {
            state,
            observerCount,
            memberCount,
            wsName,
            isInWsContext,

            onOpenNavDrawer,
            openUserProfile,
            isDevelopment,

            onNotificationsPanelToggle,
            hasStorymapAccess,
        };
    },
});

export default AppBar;
</script>

<template>
  <v-app-bar
    app
    clipped-right
    flat
    class="px-2"
    color="blueGrey05Light"
  >
    <v-app-bar-nav-icon v-if="!navDrawer" class="mr-4 blueGrey90Light--text" @click.stop="onOpenNavDrawer">
      <v-btn icon>
        <v-icon>$farBars</v-icon>
      </v-btn>
    </v-app-bar-nav-icon>

    <v-toolbar-title class="blueGrey90Light--text font-weight-bold text-h5" style="line-height: 1.333;">
      <portal-target name="header-title" />
    </v-toolbar-title>

    <v-spacer />
    <portal-target name="header-center" />
    <v-spacer />

    <portal-target v-if="$vuetify.breakpoint.mdAndUp" name="header-right" class="flex-shrink-0" />

    <v-menu
      v-else
      offset-y
      left
    >
      <template #activator="{ on: menu }">
        <v-tooltip top open-delay="250">
          <template #activator="{ on: tooltip }">
            <v-btn
              small
              icon
              v-on="{ ...tooltip, ...menu }"
            >
              <v-icon small>
                $farBars
              </v-icon>
            </v-btn>
          </template>
          <span>More options</span>
        </v-tooltip>
      </template>
      <v-card color="blueGrey05Light" class="pa-1 header-right--mobile">
        <portal-target name="header-right" />
      </v-card>
    </v-menu>
    <!-- <v-tooltip
      v-if="isDevelopment && $vuetify.breakpoint.mdAndUp"
      open-delay="250"
      class="hidden-xs-only"
      bottom
    >
      <template #activator="{ on }">
        <v-btn :to="{ name: 'devicons' }" class="mx-2 mr-3" icon>
          <v-icon color="red" v-on="on">
            $falIcons
          </v-icon>
        </v-btn>
      </template>
      <span>Check all icons in project</span>
    </v-tooltip> -->

    <v-divider class="hidden-xs-only ml-3" inset vertical />

    <v-tooltip bottom open-delay="250">
      <template #activator="{ on: tooltipOn }">
        <v-btn
          class="ml-3 mr-1"
          icon
          @click="onNotificationsPanelToggle"
          v-on="tooltipOn"
        >
          <v-badge
            v-if="notificationsCount > 0"
            bordered
            top
            color="red darken-1"
            :content="notificationsCount"
            offset-x="10"
            offset-y="10"
          >
            <v-icon>
              $bell-light
            </v-icon>
          </v-badge>
          <v-icon v-else>
            $bell-light
          </v-icon>
        </v-btn>
      </template>

      <div>Notifications</div>
    </v-tooltip>
  </v-app-bar>
</template>

<style lang="scss" scoped>
::v-deep {
    .v-toolbar__content {
        padding-left: 8px;
        padding-right: 12px;
    }

    .header-right--mobile {
        .v-btn {
            display: flex;
            margin-left: 0 !important;
            margin-right: 0;
            justify-content: flex-start;
            width: 100%;
        }
    }
}
</style>
